import { factor, isNegative, isZero, money } from '@fameacc/money';
import { z } from 'zod';
import { documentLineTypeSchema, positionstext, priceBasisSchema, taxRateFactor, taxScenarioSchema, unitPriceAmount, } from './documents.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Aborechnungsvorlagen — Auftrag `C-abocrud` (2026-09-02),
 * Task 4 des Plans `docs/superpowers/plans/2026-09-02-wiederkehrend-umsetzung.md`,
 * Spec `docs/superpowers/specs/2026-09-02-wiederkehrend-design.md` Abschnitte
 * 3.1, 3.2, 4 und 11.1.
 *
 * **Der Anlagevertrag hat KEIN Statusfeld** (`D-10`, `E-656`). Das ist die
 * Endpunkthälfte einer Zusicherung, deren Schemahälfte in Migration `0122`
 * steht: Dort ist `entwurf` die Vorgabe der Spalte, hier gibt es kein Feld,
 * über das ein Aufrufer etwas anderes vorgeben könnte. Beide Hälften braucht
 * es — eine Vorgabe allein liesse sich überschreiben, ein fehlendes Feld
 * allein liesse sich am Vertrag vorbei umgehen. Und `entwurf` als einziger
 * Anlagezustand ist die härteste Stütze des Parallelbetriebs: Solange die
 * Alt-Engine eine Vorlage fakturiert, darf das neue System für sie nichts
 * erzeugen.
 *
 * **Beträge sind Zeichenketten und werden zu `Money`/`Factor`** (Hausregel 1).
 * `unitPriceAmount` und `taxRateFactor` kommen aus `documents.ts` und werden
 * nicht nachgebaut: Die Vorlagenposition ist feldgleich zur Belegzeile
 * (`D-1`), und zwei Fassungen desselben Bausteins wären zwei Auslegungen.
 * `mengeFaktor` steht dagegen lokal — dieselbe Lage und dieselbe Wahl wie in
 * `altimport.ts`, weil `documents.ts` seine Menge nicht exportiert.
 *
 * **Was hier geschieht, ist Normalisierung und Meldung, nicht Durchsetzung.**
 * Die massgeblichen Prüfungen sind die `CHECK`-Bedingungen und die zwei
 * Trigger aus Migration `0122`; sie gelten auch gegen direktes SQL. Der
 * Vertrag bringt die Eingabe in die Form, die die Datenbank erwartet, und
 * liefert eine verständliche Meldung, bevor die Datenbank eine unverständliche
 * liefert.
 */
// --- Bausteine -------------------------------------------------------------
/** Trimmt und zieht Folgen von Leerraum auf ein Leerzeichen zusammen. */
function straffen(wert) {
    return wert.trim().replace(/\s+/g, ' ');
}
/** Ein Pflichttextfeld. Leer heisst Ablehnung, nicht Platzhalter. */
const pflichtText = (feld) => z.string().transform((wert, ctx) => {
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/** Ein optionales Textfeld: getrimmt, zusammengezogen, leer wird `null`. */
const optionalText = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/**
 * Eine Menge: Faktor, echt über null.
 *
 * Das Vorzeichen steckt im Preis und nicht in der Menge — so hält es die
 * Belegzeile (`document_line_quantity_positive`), so hält es
 * `aborechnung_vorlage_position_menge_positiv`, und zwei Orte für dasselbe
 * Vorzeichen sind einer zu viel.
 */
const mengeFaktor = z.string().transform((wert, ctx) => {
    let menge;
    try {
        menge = factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Keine gültige Menge' });
        return z.NEVER;
    }
    // Verglichen wird DEZIMAL über `packages/money` und nie über `Number` —
    // `money/no-float-money` untersagt Letzteres zu Recht, und der Umweg über
    // `Money` ist verlustfrei: `Factor` und `Money` sind beide `string`.
    const alsBetrag = money(menge);
    if (isNegative(alsBetrag) || isZero(alsBetrag)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Die Menge muss größer als null sein' });
        return z.NEVER;
    }
    return menge;
});
// --- Aufzählungen ----------------------------------------------------------
/**
 * Die vier Zustände einer Vorlage (`aborechnung_vorlage_status_bekannt`).
 *
 * Als LESESCHEMA vollständig, im Statuswechsel dagegen nicht: `entwurf` ist
 * ein Anlagezustand und kein Ziel (`aborechnungStatusZielSchema` unten).
 */
export const aborechnungStatusSchema = z.enum(['entwurf', 'pausiert', 'aktiv', 'beendet']);
/**
 * Die drei Regeln, aus denen der Generator den Leistungszeitraum je Position
 * rechnet (`D-13`, `K-1`).
 *
 * Sie steht JE POSITION und nicht am Kopf: `service_period_start/end` ist ein
 * Positionsfeld, und eine Regel am Kopf könnte einen Beleg mit einer Hosting-
 * und einer Einmalposition nicht abbilden.
 */
export const leistungszeitraumRegelSchema = z.enum([
    'periode_voraus',
    'periode_zurueck',
    'keiner',
]);
/**
 * Die Herkunft einer Vorlage. Über die Schnittstelle entsteht ausschliesslich
 * `neu`; `altsystem` setzt allein die Übernahme (`I-aboimport`), und deshalb
 * steht dieses Schema hier nur als LESEform.
 */
export const aborechnungHerkunftSchema = z.enum(['neu', 'altsystem']);
/** Das Ergebnis eines Laufs (`aborechnung_lauf_ergebnis_bekannt`). */
export const aborechnungLaufErgebnisSchema = z.enum(['erzeugt', 'uebersprungen', 'fehler']);
// --- Die Position ----------------------------------------------------------
/**
 * Das Objektschema einer Vorlagenposition, **vor** der Querprüfung zwischen
 * ihren Feldern.
 *
 * Getrennt gehalten, weil `.strict()` auf dem Objekt sitzt und die Querprüfung
 * eine `.transform()` darüber ist — `z.object(...).strict().transform(...)`
 * ist die einzige Reihenfolge, in der beides gilt (wortgleiche Bauart wie
 * `documentLineInputObject`).
 *
 * **Ohne `priceBasis`.** Sie steht am Vorlagenkopf, und der zusammengesetzte
 * Fremdschlüssel `(vorlage_id, price_basis)` bindet beide aneinander
 * (`E-662`). Ein Feld an der Position wäre ein Angebot, sie auseinanderlaufen
 * zu lassen — und der Fremdschlüssel wiese es ab, mit einer Meldung über einen
 * Schlüssel statt über die Sache.
 *
 * **Ohne `position`.** Die Reihenfolge ist die der Liste; eine mitgegebene
 * Nummer wäre eine zweite Wahrheit über dieselbe Reihenfolge und liesse
 * Lücken und Dubletten zu, die `aborechnung_vorlage_position_positiv` nicht
 * fängt.
 *
 * **Ohne `servicePeriodStart/End`.** Der Leistungszeitraum entsteht erst beim
 * Erzeugen, aus `leistungszeitraumRegel` und dem Stichtag (`D-13`, `E-677`).
 */
const aborechnungPositionInputObject = z
    .object({
    lineType: documentLineTypeSchema.default('item'),
    // `positionstext` und nicht das lokale `pflichtText` (Auftrag `PT-vertrag`,
    // Task 2 der Positionstext-Spec, Abschnitt 5.3, `D-3`): Die Erfassungsprüfung
    // gilt für beide Positionsverträge gleich, und die Vorlagenposition ist
    // feldgleich zur Belegzeile (`D-1` der Wiederkehrend-Spec) — importiert aus
    // `documents.ts` statt nachgebaut, derselbe Weg wie `unitPriceAmount`.
    description: positionstext('description'),
    quantity: mengeFaktor.optional().nullable(),
    unitCode: optionalText,
    unitPrice: unitPriceAmount.optional().nullable(),
    taxRate: taxRateFactor.optional().nullable(),
    taxKey: optionalText,
    revenueAccountNumber: optionalText,
    leistungszeitraumRegel: leistungszeitraumRegelSchema.default('keiner'),
})
    .strict();
/**
 * Eine Vorlagenposition.
 *
 * **Der eine bewusste Unterschied zur Belegzeile:** `taxKey` und
 * `revenueAccountNumber` dürfen hier fehlen. Die zwanzig Alt-Vorlagen bringen
 * beide nicht mit — das Altsystem hat die Felder nicht (Spec 9). Verlangte
 * dieser Vertrag sie schon beim Erfassen, könnte die Übernahme keine einzige
 * Zeile anlegen, und die Riegelbedingung `E-682` wäre unerreichbar: Es gäbe
 * nie eine Vorlage, der sie fehlten.
 *
 * **Pflicht werden sie beim Scharfschalten** und nicht beim Erfassen
 * (`E-682`) — das ist die einzige Stelle, an der die Unvollständigkeit
 * auffällt, BEVOR eine Rechnung sie trägt.
 *
 * `taxRate` gehört ausdrücklich NICHT in diese Gruppe: Ein Satz, der fehlen
 * darf, wäre ein Beleg ohne Steuersatz. Die Übernahme setzt ihn aus dem
 * Alt-Feld, und der Generator hält ihn später gegen den gültigen Satz
 * (`D-12`, `E-674`).
 */
export const aborechnungPositionInputSchema = aborechnungPositionInputObject.transform((wert, ctx) => {
    const normalisiert = {
        ...wert,
        quantity: wert.quantity ?? null,
        unitPrice: wert.unitPrice ?? null,
        taxRate: wert.taxRate ?? null,
    };
    if (normalisiert.lineType === 'text') {
        const besetzt = [
            ['quantity', normalisiert.quantity],
            ['unitCode', normalisiert.unitCode],
            ['unitPrice', normalisiert.unitPrice],
            ['taxRate', normalisiert.taxRate],
            ['taxKey', normalisiert.taxKey],
            ['revenueAccountNumber', normalisiert.revenueAccountNumber],
        ].filter(([, v]) => v !== null);
        if (besetzt.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [besetzt[0]?.[0] ?? 'lineType'],
                message: 'Eine Textzeile trägt weder Menge noch Einheit, Preis, Satz, Schlüssel noch Konto',
            });
            return z.NEVER;
        }
        // `aborechnung_vorlage_position_text` verlangt zusätzlich
        // `leistungszeitraum_regel = 'keiner'`: Eine Textzeile ist keine
        // Leistung und hat deshalb keinen Zeitraum.
        if (normalisiert.leistungszeitraumRegel !== 'keiner') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['leistungszeitraumRegel'],
                message: 'Eine Textzeile trägt keinen Leistungszeitraum',
            });
            return z.NEVER;
        }
        return normalisiert;
    }
    const fehlend = [
        ['quantity', normalisiert.quantity],
        ['unitCode', normalisiert.unitCode],
        ['unitPrice', normalisiert.unitPrice],
        ['taxRate', normalisiert.taxRate],
    ].filter(([, v]) => v === null);
    if (fehlend.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [fehlend[0]?.[0] ?? 'lineType'],
            message: 'Eine Sachposition verlangt Menge, Einheit, Einzelpreis und Steuersatz',
        });
        return z.NEVER;
    }
    if (normalisiert.revenueAccountNumber !== null &&
        !/^[0-9]{4,6}$/.test(normalisiert.revenueAccountNumber)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['revenueAccountNumber'],
            message: 'Kontonummer besteht aus vier bis sechs Ziffern',
        });
        return z.NEVER;
    }
    return normalisiert;
});
// --- Der Kopf --------------------------------------------------------------
/**
 * Das Intervall in Monaten (`aborechnung_vorlage_intervall_bereich`).
 *
 * Ganzzahl und keine Zeichenkette: Ein Intervall ist eine Anzahl und kein
 * Betrag — `money/no-float-money` zielt auf Geld, und eine Monatszahl durch
 * `Money` zu schicken machte aus einer Anzahl eine Währungsgrösse.
 */
const intervallMonate = z
    .number()
    .int('Das Intervall ist eine ganze Zahl von Monaten')
    .min(1, 'Das Intervall ist mindestens ein Monat')
    .max(36, 'Das Intervall ist höchstens 36 Monate');
/** Der gespeicherte Anker (`aborechnung_vorlage_ankertag_bereich`, `D-8`). */
const ankertag = z
    .number()
    .int('Der Ankertag ist eine ganze Zahl')
    .min(1, 'Der Ankertag liegt zwischen 1 und 31')
    .max(31, 'Der Ankertag liegt zwischen 1 und 31');
/** Das Zahlungsziel in Tagen (`aborechnung_vorlage_zahlungsziel_bereich`, `D-20`). */
const zahlungszielTage = z
    .number()
    .int('Das Zahlungsziel ist eine ganze Zahl von Tagen')
    .min(0, 'Das Zahlungsziel liegt zwischen 0 und 90 Tagen')
    .max(90, 'Das Zahlungsziel liegt zwischen 0 und 90 Tagen');
const aborechnungAnlageObject = z
    .object({
    // Pflicht und ohne Ausweichweg (`D-14`). ANSCHRIFTSFELDER GIBT ES NICHT --
    // nicht ein optionales, das niemand füllt, sondern gar keines. Eine
    // kopierte Anschrift an der Vorlage wäre eine zweite Wahrheit über den
    // Kunden, die zwölf Monate alt werden kann; der Generator holt sie beim
    // Erzeugen aus dem Kundenstamm wie bei jedem anderen Beleg.
    customerId: uuid,
    bezeichnung: pflichtText('bezeichnung'),
    priceBasis: priceBasisSchema,
    intervallMonate,
    ankertag,
    naechsterStichtag: isoDate,
    letzterStichtag: isoDate.optional().nullable(),
    // Vorbelegt und sichtbar (`D-20`): Die Alt-Engine setzt hart `+7 day`, und
    // ein Feld macht aus der versteckten Konstante eine Vertragsangabe.
    zahlungszielTage: zahlungszielTage.default(7),
    // Dasselbe Muster und derselbe Grund (`M-abotaxscenario`, 2026-09-03):
    // Der Generator hat `'domestic'` bis dahin ANGENOMMEN und jede
    // Nicht-Inlandsvorlage mit Befund übersprungen. Die Vorgabe steht hier,
    // damit der häufige Fall ohne Angabe auskommt und der seltene überhaupt
    // ausdrückbar wird — `taxScenarioSchema` ist dieselbe Aufzählung wie am
    // Beleg, weil der Generator den Wert unverändert weiterreicht.
    taxScenario: taxScenarioSchema.default('domestic'),
    positionen: z.array(aborechnungPositionInputSchema).optional(),
})
    .strict();
/**
 * Eine neue Vorlage, wie der Dienst sie entgegennimmt.
 *
 * **Kein `status`, kein `herkunft`, kein `externalId`.** Der Status ist immer
 * `entwurf` (`E-656`); `herkunft` ist über diese Strecke immer `'neu'`, und
 * `external_id` gehört allein der Übernahme (`I-aboimport`). Ein Feld dafür
 * wäre der Weg, über den eine hier entstandene Vorlage sich als Alt-Vorlage
 * ausgeben und den Eindeutigkeitsindex `aborechnung_vorlage_extern_einmal`
 * belegen könnte.
 */
export const aborechnungAnlageSchema = aborechnungAnlageObject.transform((wert, ctx) => {
    pruefeEnde(wert.naechsterStichtag, wert.letzterStichtag ?? null, ctx);
    return { ...wert, letzterStichtag: wert.letzterStichtag ?? null };
});
/**
 * Der Änderungssatz einer Vorlage — nur in `entwurf` und `pausiert` wirksam
 * (`D-11`, `E-659`).
 *
 * **Ohne `priceBasis`.** Der zusammengesetzte Fremdschlüssel der Positionen
 * steht auf `ON UPDATE RESTRICT`, und das ist die tragende Hälfte: Mit
 * `CASCADE` liesse sich die Preisbasis umstellen und die Positionen zögen
 * stillschweigend mit — ihre Beträge bedeuteten danach etwas anderes, ohne
 * dass sich eine Zahl geändert hätte. Wer die Basis wechseln will, legt eine
 * neue Vorlage an; ein eigener Umrechnungsvorgang wie am Beleg
 * (`POST /documents/:id/price-basis`) ist für eine Vorlage nicht gebaut.
 *
 * **Mit `customerId`.** Anders als am Beleg ist der Kundenwechsel hier
 * harmlos: Die Vorlage führt keinen eingefrorenen Kundensatz, den ein Wechsel
 * zur Kopie eines anderen Kunden machte (`D-14`) — sie führt einen Verweis,
 * und der darf sich ändern, solange nichts erzeugt wurde.
 *
 * Jedes Feld ist freiwillig; `.strict()` ist hier der Prüfstein, weil ein
 * vertippter Feldname sonst still verschluckt würde.
 */
export const aborechnungPatchSchema = z
    .object({
    customerId: uuid.optional(),
    bezeichnung: pflichtText('bezeichnung').optional(),
    intervallMonate: intervallMonate.optional(),
    ankertag: ankertag.optional(),
    naechsterStichtag: isoDate.optional(),
    // `null` heisst «kein Ende», `undefined` heisst «nicht ändern».
    letzterStichtag: z.union([isoDate, z.null()]).optional(),
    zahlungszielTage: zahlungszielTage.optional(),
    // Änderbar wie jede andere Vertragsangabe — und damit ausschliesslich in
    // `entwurf` und `pausiert`. Eine AKTIVE Vorlage kann ihr Szenario nicht
    // wechseln; das hält die Positivliste in
    // `assert_aborechnung_vorlage_uebergang()` (`0122`) ohne eigenes Zutun,
    // und der Weg ist derselbe wie bei jedem anderen Feld: pausieren, ändern,
    // reaktivieren. **Kein `null`** — anders als `letzterStichtag` hat dieses
    // Feld keinen leeren Zustand, den man wiederherstellen könnte.
    taxScenario: taxScenarioSchema.optional(),
})
    .strict();
/**
 * Die vollständige Ersetzung der Positionsmenge.
 *
 * **Ersetzen und nicht Anfügen** — es gibt kein `addPosition`, dieselbe Wahl
 * wie bei `PUT /documents/:id/lines`. Ersetzen heisst, dass Überzähliges
 * fortkommt; genau dafür vergibt Migration `0129` das `DELETE`-Recht auf
 * `aborechnung_vorlage_position` (`E-162`, revidiert am 2026-09-02).
 *
 * Eine leere Liste ist zulässig: Eine Vorlage ohne Positionen ist ein
 * legitimer Zwischenzustand des Entwurfs. Dass eine SCHARFGESCHALTETE Vorlage
 * vollständig kontiert sein muss, hält `E-682` an der einen Stelle, an der es
 * zählt.
 */
export const aborechnungPositionenSchema = z
    .object({
    positionen: z.array(aborechnungPositionInputSchema),
})
    .strict();
/**
 * Der Statuswechsel.
 *
 * **`entwurf` ist kein Ziel.** Wer eine scharfgeschaltete Vorlage in den
 * Entwurf zurückstellen könnte, hätte die Scharfschaltung (`E-657`)
 * rückgängig gemacht, ohne dass es jemand sieht — dieselbe Aussage, die
 * `assert_aborechnung_vorlage_uebergang()` in `0122` hält. Der Vertrag lehnt
 * hier ab, damit die Meldung von der Sache spricht und nicht von einem
 * Triggernamen.
 */
export const aborechnungStatusWechselSchema = z
    .object({
    status: z.enum(['aktiv', 'pausiert', 'beendet']),
})
    .strict();
/** Der Filter der Liste (Spec 11.1): Status und Kunde, beide freiwillig. */
export const aborechnungFilterSchema = z
    .object({
    status: aborechnungStatusSchema.optional(),
    customerId: uuid.optional(),
})
    .strict();
/**
 * Das Ende darf nicht vor dem nächsten Stichtag liegen
 * (`aborechnung_vorlage_ende_nach_stichtag`).
 *
 * Der Statusteil jener Bedingung — `status = 'beendet'` hebt sie auf — kommt
 * hier nicht vor, und das ist richtig: Über die Anlage entsteht ausschliesslich
 * `entwurf`, und über den Änderungssatz ausschliesslich `entwurf` oder
 * `pausiert`. Die Ausnahme gehört dem Generator, der nach dem letzten
 * Durchgang fortschreibt und dabei beendet (`E-678`).
 */
function pruefeEnde(naechster, letzter, ctx) {
    if (letzter === null || letzter >= naechster)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['letzterStichtag'],
        message: 'Der letzte Stichtag liegt vor dem nächsten',
    });
}
// --- Die Lesesichten -------------------------------------------------------
/** Eine Position, wie die Antwort sie zeigt. */
export const aborechnungPositionAnsichtSchema = z
    .object({
    position: z.number().int(),
    lineType: documentLineTypeSchema,
    description: z.string(),
    // Beträge verlassen die Antwort als Zeichenketten in kanonischer Form —
    // nie als Zahl, aus demselben Grund, aus dem sie als Zeichenkette
    // hereinkommen (Hausregel 1).
    quantity: z.string().nullable(),
    unitCode: z.string().nullable(),
    unitPrice: z.string().nullable(),
    taxRate: z.string().nullable(),
    taxKey: z.string().nullable(),
    revenueAccountNumber: z.string().nullable(),
    leistungszeitraumRegel: leistungszeitraumRegelSchema,
})
    .strict();
/**
 * Der Grundkatalog der Aufhebung — geschlossen, wie der `CHECK`
 * `aborechnung_lauf_aufhebung_grund_bekannt` (Migration `0166`).
 */
export const aborechnungLaufAufhebungGrundSchema = z.enum([
    'periode_wiedereroeffnet',
    'vorlage_korrigiert',
    'fehler_behoben',
]);
/** Zwei Zustände, kein dritter — eine Aufhebung bucht nicht (`0166`). */
export const aborechnungLaufAufhebungStatusSchema = z.enum(['angelegt', 'freigegeben']);
/**
 * Der Stand einer Aufhebung, wie ihn eine Laufzeile mitträgt — `null`, wenn
 * noch keine existiert.
 *
 * **`id` ist die Kennung der ANLAGE-Zeile**, dieselbe Bauform wie
 * `guthabenKorrekturSchema`: Der Freigabe-Endpunkt nimmt sie im Pfad, egal ob
 * die Aufhebung selbst noch `angelegt` oder schon `freigegeben` ist.
 */
export const aborechnungLaufAufhebungStandSchema = z
    .object({
    id: uuid,
    status: aborechnungLaufAufhebungStatusSchema,
    grund: aborechnungLaufAufhebungGrundSchema,
    begruendung: z.string(),
    angelegtVon: uuid,
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): Wer die Aufhebung angelegt hat, hat sie
     * auch freigegeben — nur unter einer Freigaberegel ohne vier Augen.
     * `optional()`, weil ältere Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
})
    .strict();
/** Eine Zeile der Lauf-Historie (`aborechnung_lauf`, Migration `0123`). */
export const aborechnungLaufAnsichtSchema = z
    .object({
    id: uuid,
    periodeStichtag: z.string(),
    ergebnis: aborechnungLaufErgebnisSchema,
    documentId: uuid.nullable(),
    uebersprungenePerioden: z.number().int(),
    vermerk: z.string().nullable(),
    erzeugtAm: z.string(),
    // Nur relevant bei `ergebnis: 'fehler'`; für 'erzeugt'/'uebersprungen'
    // immer `null` (`0166`, `I-abolaufupdate`).
    aufhebung: aborechnungLaufAufhebungStandSchema.nullable(),
})
    .strict();
/**
 * Der Kopf einer Vorlage, wie Liste und Einzelauskunft ihn zeigen.
 *
 * **Die letzten vier Felder stehen nur in der LISTE** (`E-762`, Auftrag
 * `C-abolistesummen`, 2026-09-03) — deshalb `.optional()` und nicht als
 * eigener, zweiter Kopftyp. Ein zweiter Typ hätte denselben Vertrag ein
 * zweites Mal ausgeschrieben und der Schnittstellenbeschreibung ihre
 * gemeinsame Komponente `AborechnungVorlageKopf` genommen — ein neues
 * optionales Feld an einem bestehenden Namen ist additiv, ein umbenanntes
 * Schema ist es nicht (`pnpm api:spec:check`, Regel 8: „Eine Komponente ist
 * weg"). `zeileZuKopf()` (`aborechnungen.service.ts`) lässt die vier Felder
 * für die Einzelauskunft, die Anlage, die Änderung und den Statuswechsel
 * einfach WEG statt sie mit `undefined` zu belegen — `exactOptionalPropertyTypes`
 * unterscheidet beides, und nur das Weglassen ist „nicht gesetzt".
 *
 * **Warum diese vier Felder überhaupt existieren.** Die Oberfläche holte
 * Kunde und Positionen bis dahin je Listenzeile selbst nach — bis zu 40
 * Kleinabfragen bei 20 Verträgen — und rechnete Netto/Brutto im Browser.
 * `apps/web/src/aborechnungen/summen.ts` trug dafür einen Kopfkommentar mit
 * Verfallsdatum: eine Vorschau, keine Wahrheit. Diese vier Felder lösen das
 * Versprechen ein.
 *
 * **`customerDisplayNo`/`customerLegalName` sind LIVE gelesen und nicht
 * eingefroren.** Anders als `document.billing_legal_name` (die beim
 * Ausstellen eingefrorene Anschrift eines BELEGS) ist eine Vorlage kein
 * Beleg — sie führt einen Verweis auf den Kunden, der sich ändern darf
 * (`aborechnungPatchSchema` erlaubt `customerId`), und die Liste zeigt
 * deshalb den Kundenstamm, wie er JETZT steht.
 *
 * **`nettoSumme`/`bruttoSumme` kommen aus derselben Arithmetik wie ein
 * Beleg** — `computeDocumentAmounts` aus
 * `apps/api/src/documents/document-arithmetic.ts`, angewandt auf die
 * aktuellen Sachpositionen der Vorlage. Eine Rundung je Position, ein
 * Steuerbetrag je `(Satz, Schlüssel)`-Gruppe, keine Rundung auf der Summe —
 * dieselbe Ebenentrennung, dieselbe Funktion, kein zweiter Rechenweg.
 */
export const aborechnungVorlageKopfSchema = z
    .object({
    id: uuid,
    customerId: uuid,
    bezeichnung: z.string(),
    documentType: z.literal('recurring_invoice'),
    priceBasis: priceBasisSchema,
    intervallMonate: z.number().int(),
    ankertag: z.number().int(),
    naechsterStichtag: z.string(),
    letzterStichtag: z.string().nullable(),
    status: aborechnungStatusSchema,
    zahlungszielTage: z.number().int(),
    // Was der Generator an `document.tax_scenario` weiterreicht (`E-761`).
    // Am Beleg steht daneben `tax_scenario_source`; hier nicht — eine Vorlage
    // trägt ausschliesslich Angaben, nie Ableitungen, und ein Feld, das immer
    // `'declared'` hielte, wäre eine Unterscheidung ohne Unterschied.
    taxScenario: taxScenarioSchema,
    herkunft: aborechnungHerkunftSchema,
    externalId: z.string().nullable(),
    createdAt: z.string(),
    createdBy: uuid,
    updatedAt: z.string(),
    updatedBy: uuid,
    // Nur in der Liste gesetzt (`E-762`) — siehe Kopfkommentar oben.
    customerDisplayNo: z.string().optional(),
    customerLegalName: z.string().optional(),
    nettoSumme: centAmount.optional(),
    bruttoSumme: centAmount.optional(),
})
    .strict();
/**
 * Die Einzelauskunft: Kopf, Positionen und die Lauf-Historie (Spec 11.1).
 *
 * Die Historie steht mit dabei und nicht hinter einem zweiten Endpunkt: Sie
 * ist die Antwort auf «was hat diese Vorlage bisher erzeugt», und wer eine
 * Vorlage aufschlägt, stellt genau diese Frage. Eine Vorlage hat je Periode
 * höchstens eine Zeile; die Liste wächst also mit dem Kalender und nicht mit
 * dem Betrieb.
 */
export const aborechnungVorlageAnsichtSchema = aborechnungVorlageKopfSchema
    .extend({
    positionen: z.array(aborechnungPositionAnsichtSchema),
    laeufe: z.array(aborechnungLaufAnsichtSchema),
})
    .strict();
/** Die Liste. Nur Köpfe (plus Kundenanzeige und Summen, `E-762`) — Positionen und Historie holt die Einzelauskunft. */
export const aborechnungVorlageListeSchema = z
    .object({
    vorlagen: z.array(aborechnungVorlageKopfSchema),
})
    .strict();
// --- Aufhebung einer fehler-Laufzeile (Migration 0166, Auftrag
// I-abolaufupdate, 2026-09-05) ----------------------------------------------
//
// `POST /api/v1/aborechnungen/laeufe/:id/aufhebung` — die Anlage. Bucht
// nicht: Sie hält nur Grund und Begründung fest und wartet auf die Freigabe
// durch eine zweite Person (Vier-Augen, `aborechnung_lauf_aufhebung_vier_augen`,
// `0166`).
export const aborechnungLaufAufhebungCreateRequestSchema = z
    .object({
    grund: aborechnungLaufAufhebungGrundSchema,
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000),
})
    .strict();
/**
 * `POST /api/v1/aborechnungen/laeufe/aufhebung/:id/freigabe` — der Rumpf.
 *
 * **Leer, mit Absicht.** Grund und Begründung stehen bereits an der Anlage
 * und bleiben über die Kette unverändert (`aborechnung_lauf_aufhebung_kette`,
 * `0166`); die Freigabe ist ein Ja oder ein Nein einer zweiten Person und
 * trägt keine eigenen Sachangaben. Kein `idempotencyKey`: Die Freigabe bucht
 * nicht (sie hebt nur die Belegung der Periode auf), es gibt also keine
 * Buchung, deren Wiederholung ein Schlüssel abfangen müsste — eine zweite
 * Freigabe derselben Aufhebung ist `ABORECHNUNG_LAUF_AUFHEBUNG_ALREADY_APPROVED`.
 */
export const aborechnungLaufAufhebungFreigabeRequestSchema = z.object({}).strict();
//# sourceMappingURL=aborechnungen.js.map